import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import HeaderComponent from "./components/appbar/HeaderComponent";
import HomeLandingPage from "./components/home/HomeLandingPage";
import ListPostsComponent from "./components/posts/ListPostsComponent";
import AuthenticatedRoute from "./components/auth/AuthenticatedRoute";
import AdminRoute from "./components/auth/AdminRoute";
import './animations.css';

// Less visited / heavier pages (rich text editor, forms) are split into separate chunks
// so the first load on mobile only downloads what the job list needs.
const RegisterComponent = lazy(() => import("./components/auth/RegisterComponent"));
const LandingPage = lazy(() => import("./components/auth/LandingPage"));
const ListYourPostsComponent = lazy(() => import("./components/posts/ListYourPostsComponent"));
const ManageUserComponent = lazy(() => import("./components/user/ManageUserComponent"));
const CandidateComponent = lazy(() => import("./components/posts/CandidateComponent"));
const NotFoundPage = lazy(() => import("./components/notfound/NotFoundPage"));
const ListFavouritesPostsComponent = lazy(() => import("./components/posts/ListFavouritesPostsComponent"));
const UpsertComponent = lazy(() => import("./components/posts/UpsertComponent"));
const PostDetailsComponent = lazy(() => import("./components/posts/PostDetailsComponent"));
const ManageTalentUserComponent = lazy(() => import("./components/user/ManageTalentUserComponent"));
const UserTalentDirectContact = lazy(() => import("./components/userTalent/UserTalentDirectContact"));
const AdminPage = lazy(() => import("./components/admin/AdminPage"));
const ForgotPasswordPage = lazy(() => import("./components/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./components/auth/ResetPasswordPage"));
const LegalPage = lazy(() => import("./components/legal/LegalPage"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
}

const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
    <CircularProgress />
  </Box>
);

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <HeaderComponent>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomeLandingPage />} />
            <Route path="/posts" element={<ListPostsComponent />} />
            <Route path="/jobs" element={<ListPostsComponent />} />
            <Route path="/jobs/in-:nationTag" element={<ListPostsComponent />} />
            <Route path="/jobs/remote" element={<ListPostsComponent isRemoteOnly={true} />} />
            <Route path="/jobs/:roleSlug/in-:nationTag" element={<ListPostsComponent />} />
            <Route path="/jobs/:roleSlug" element={<ListPostsComponent />} />
            <Route path="/add-post" element={<AuthenticatedRoute><UpsertComponent /></AuthenticatedRoute>} />
            <Route path="/details" element={<PostDetailsComponent />} />
            <Route path="/manage-user" element={<AuthenticatedRoute><ManageUserComponent /></AuthenticatedRoute>} />
            <Route path="/manage-talent-user" element={<AuthenticatedRoute><ManageTalentUserComponent /></AuthenticatedRoute>} />
            <Route path="/your-posts" element={<AuthenticatedRoute><ListYourPostsComponent /></AuthenticatedRoute>} />
            <Route path="/favourites" element={<AuthenticatedRoute><ListFavouritesPostsComponent /></AuthenticatedRoute>} />
            <Route path="/candidate" element={<AuthenticatedRoute><CandidateComponent /></AuthenticatedRoute>} />
            <Route path="/talent-user-direct-contact" element={<AuthenticatedRoute><UserTalentDirectContact /></AuthenticatedRoute>} />
            <Route path="/edit-post/:id/:switchChecked" element={<AuthenticatedRoute><UpsertComponent /></AuthenticatedRoute>} />
            <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="/privacy" element={<LegalPage doc="privacy" />} />
            <Route path="/terms" element={<LegalPage doc="terms" />} />
            <Route path="/register" element={<RegisterComponent />} />
            <Route path="/login" element={<LandingPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </HeaderComponent>
    </BrowserRouter>
  );
}

export default App;
